import React from 'react';
import {
    DossiersCollection,
    OrderInformations,
    useSelectedOrderContext,
} from '@europrocurement/l2d-modules';
import { Box } from '@mui/material';
import { CollectionDeDossierJsonldCollectiondossierRead } from '@europrocurement/l2d-domain/openApi/ApiFormalite';

const InformationsTab: React.FunctionComponent = function () {
    const { order, dossiers } = useSelectedOrderContext();

    /**
     * Détermine la date de mise à jour la plus récente parmi une collection de dossiers.
     *
     * @param dossiers - Collection de dossiers contenant chacun une propriété dateMaj
     * @returns La date de mise à jour la plus récente, ou undefined si aucun dossier valide n'est trouvé
     *
     * @example
     * ```typescript
     * const dossiers = [
     *   { dateMaj: '2024-01-15' },
     *   { dateMaj: '2024-02-20' },
     *   { dateMaj: '2024-01-30' }
     * ];
     * const lastUpdate = getLastUpdateDate(dossiers); // '2024-02-20'
     * ```
     */
    const getLastUpdateDate = (
        dossiersFromOrder: CollectionDeDossierJsonldCollectiondossierRead['dossiers'],
    ): string | undefined => {
        const validDates = dossiersFromOrder
            ?.map((dossierFromOrder) => dossierFromOrder.dateMaj)
            .filter((date): date is string => Boolean(date));

        return validDates?.length
            ? validDates.reduce((latest, current) => (current > latest ? current : latest))
            : undefined;
    };

    /**
     * Détermine la date de création la plus ancienne parmi une collection de dossiers.
     *
     * @param dossiers - Collection de dossiers contenant chacun une propriété dateCreation
     * @returns La date de mise à jour la plus récente, ou undefined si aucun dossier valide n'est trouvé
     *
     * @example
     * ```typescript
     * const dossiers = [
     *   { dateCreation: '2024-01-15' },
     *   { dateCreation: '2024-02-20' },
     *   { dateCreation: '2024-01-30' }
     * ];
     * const firstCreate = getFirstCreateDate(dossiers); // '2024-01-15'
     * ```
     */
    const getFirstCreateDate = (
        dossiersFromOrder: CollectionDeDossierJsonldCollectiondossierRead['dossiers'],
    ): string | undefined => {
        const validDates = dossiersFromOrder
            ?.map((dossierFromOrder) => dossierFromOrder.dateCreation)
            .filter((date): date is string => Boolean(date));

        return validDates?.length
            ? validDates.reduce((latest, current) => (current < latest ? current : latest))
            : undefined;
    };

    return (
        <Box data-testid="order-information-tab">
            <OrderInformations
                dateCreate={getFirstCreateDate(order.dossiers)}
                dateUpdate={getLastUpdateDate(order.dossiers)}
            />
            {order.numero && order.dossiers ? (
                <DossiersCollection
                    orderNumber={order.numero}
                    dossiers={dossiers}
                />
            ) : null}
        </Box>
    );
};

export default InformationsTab;
